import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { validateVoucher } from '../api';
import { VOUCHER_TYPE } from '../enums/voucher_type';
import { MANDATORY_DISCOUNT_TYPES } from '../enums/mandatory_discounts';

const VoucherApplication = ({ 
  onVoucherApplied, 
  onVoucherRemoved, 
  appliedVoucher, 
  bookingTotal,
  roomRate,
  hasAnyMandatoryDiscount = false,
  selectedRoomTypeId = null,
  checkInDate = null
}) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [validatedVoucher, setValidatedVoucher] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (appliedVoucher) {
      setValidatedVoucher(appliedVoucher);
      setVoucherCode(appliedVoucher.code);
    }
  }, [appliedVoucher]);

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim()) {
      setError('Please enter a voucher code');
      return;
    }

    // Check if mandatory discount is already applied
    if (hasAnyMandatoryDiscount) {
      setError('Cannot apply voucher when mandatory discount is active. Please remove mandatory discount first.');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // Pass room_type_id and check_in_date to backend for validation
      console.log('Validating voucher with room_type_id:', selectedRoomTypeId, 'and check_in_date:', checkInDate);
      const voucher = await validateVoucher(voucherCode.trim().toUpperCase(), selectedRoomTypeId, checkInDate);
      
      // Calculate discount amount based on voucher type
      let discountAmount = 0;
      if (voucher.voucher_type === VOUCHER_TYPE.BOOKING) {
        // For BOOKING vouchers, discount is one night's stay
        discountAmount = roomRate || 0;
      } else if (voucher.voucher_type === VOUCHER_TYPE.DISCOUNT) {
        // For DISCOUNT vouchers, calculate based on discount_type
        if (voucher.discount_type === 'PERCENTAGE') {
          // Calculate percentage discount on room rate
          discountAmount = (roomRate || 0) * (parseFloat(voucher.discount_value) / 100);
        } else {
          // AMOUNT type - use discount_value directly, but not more than room rate
          discountAmount = Math.min(parseFloat(voucher.discount_value), roomRate || 0);
        }
      }

      const voucherWithDiscount = {
        ...voucher,
        discountAmount
      };

      console.log('Validated voucher:', voucherWithDiscount);

      setValidatedVoucher(voucherWithDiscount);
      setError('');
      
      if (onVoucherApplied) {
        onVoucherApplied(voucherWithDiscount);
      }
    } catch (error) {
      console.error('Error validating voucher:', error);
      const errorMessage = error.response?.data?.error || 'Invalid voucher code';
      setError(errorMessage);
      setValidatedVoucher(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveVoucher = () => {
    setConfirmAction(() => () => {
      setVoucherCode('');
      setValidatedVoucher(null);
      setError('');
      
      if (onVoucherRemoved) {
        onVoucherRemoved();
      }
      setShowConfirmDialog(false);
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = () => {
    if (confirmAction) {
      confirmAction();
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleValidateVoucher();
    }
  };

  const getVoucherTypeDisplay = (type) => {
    switch (type) {
      case VOUCHER_TYPE.BOOKING:
        return 'Free One Night Stay';
      case VOUCHER_TYPE.DISCOUNT:
        return 'Discount Voucher';
      case VOUCHER_TYPE.RESTAURANT:
        return 'Restaurant Voucher';
      default:
        return type;
    }
  };

  const isDisabledDueToMandatoryDiscount = hasAnyMandatoryDiscount;

  return (
    <>
      <div style={{
        marginTop: 16,
        padding: '15px',
        background: isDisabledDueToMandatoryDiscount ? '#f8f9fa' : '#e3f2fd',
        border: isDisabledDueToMandatoryDiscount ? '2px solid #dee2e6' : '2px solid #2196f3',
        borderRadius: 8,
        opacity: isDisabledDueToMandatoryDiscount ? 0.6 : 1,
        position: 'relative'
      }}>
        {isDisabledDueToMandatoryDiscount && (
          <div style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: '#dc3545',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: '0.8em',
            fontWeight: 'bold'
          }}>
            DISABLED
          </div>
        )}
        
        <h4 style={{ 
          margin: '0 0 12px 0', 
          color: isDisabledDueToMandatoryDiscount ? '#6c757d' : '#1976d2',
          fontSize: '1.1em'
        }}>
          🎫 Voucher Code
        </h4>

        {isDisabledDueToMandatoryDiscount && (
          <div style={{
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: 6,
            padding: '12px',
            marginBottom: 12,
            fontSize: '0.9em',
            color: '#721c24'
          }}>
            ⚠️ <strong>Voucher application is disabled when mandatory discount is active.</strong>
            <br />Remove the mandatory discount to apply a voucher.
          </div>
        )}

      {!validatedVoucher ? (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              placeholder="Enter voucher code"
              maxLength="12"
              style={{
                flex: 1,
                padding: '8px 12px',
                border: isDisabledDueToMandatoryDiscount ? '2px solid #dee2e6' : '2px solid #2196f3',
                borderRadius: 4,
                textTransform: 'uppercase',
                fontSize: '1rem',
                lineHeight: '1.4',
                boxSizing: 'border-box',
                backgroundColor: isDisabledDueToMandatoryDiscount ? '#e9ecef' : '#f3f9ff',
                color: isDisabledDueToMandatoryDiscount ? '#6c757d' : 'inherit',
                cursor: isDisabledDueToMandatoryDiscount ? 'not-allowed' : 'text'
              }}
              disabled={isValidating || isDisabledDueToMandatoryDiscount}
            />
            <button
              onClick={handleValidateVoucher}
              disabled={isValidating || !voucherCode.trim() || isDisabledDueToMandatoryDiscount}
              style={{
                backgroundColor: (isValidating || isDisabledDueToMandatoryDiscount) ? '#6c757d' : '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '8px 16px',
                cursor: (isValidating || isDisabledDueToMandatoryDiscount) ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: '1.4',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
                marginBottom: '28px'
              }}
            >
              {isValidating ? 'Validating...' : 'Apply'}
            </button>
          </div>

          {error && (
            <div style={{
              color: '#dc3545',
              fontSize: '0.9em',
              marginTop: 4,
              padding: '4px 8px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: 4
            }}>
              {error}
            </div>
          )}

          <div style={{ 
            fontSize: '0.85em', 
            color: isDisabledDueToMandatoryDiscount ? '#6c757d' : '#1976d2', 
            marginTop: 8,
            fontStyle: 'italic',
            background: isDisabledDueToMandatoryDiscount ? '#e9ecef' : '#f3f9ff',
            padding: 8,
            borderRadius: 4,
            borderLeft: `4px solid ${isDisabledDueToMandatoryDiscount ? '#dee2e6' : '#2196f3'}`
          }}>
            💡 Enter a valid voucher code to apply discount to your booking.
          </div>
        </div>
      ) : (
        <div style={{
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: 4,
          padding: 12
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 600,
                color: '#155724',
                marginBottom: 4
              }}>
                ✓ {validatedVoucher.code} Applied
              </div>
              
              <div style={{
                fontSize: '0.9em',
                color: '#155724',
                marginBottom: 4
              }}>
                {validatedVoucher.title}
              </div>
              
              <div style={{
                fontSize: '0.8em',
                color: '#155724'
              }}>
                Type: {getVoucherTypeDisplay(validatedVoucher.voucher_type)}
              </div>

              {validatedVoucher.voucher_type === VOUCHER_TYPE.DISCOUNT && (
                <div style={{
                  fontSize: '0.8em',
                  color: '#155724',
                  marginTop: 4
                }}>
                  {validatedVoucher.discount_type === 'PERCENTAGE' ? (
                    <>Discount: {validatedVoucher.discount_value}% off</>
                  ) : (
                    <>Discount: ₱{parseFloat(validatedVoucher.discount_value).toFixed(2)} off</>
                  )}
                </div>
              )}

              {validatedVoucher.voucher_type !== VOUCHER_TYPE.RESTAURANT && (
                <div style={{
                  fontSize: '0.9em',
                  fontWeight: 600,
                  color: '#155724',
                  marginTop: 8
                }}>
                  Applied Discount: ₱{parseFloat(validatedVoucher.discountAmount || 0).toFixed(2)}
                </div>
              )}

              {validatedVoucher.voucher_type === VOUCHER_TYPE.RESTAURANT && (
                <div style={{
                  fontSize: '0.8em',
                  color: '#856404',
                  marginTop: 4,
                  fontStyle: 'italic'
                }}>
                  Note: This voucher is for restaurant use only and does not affect booking total.
                </div>
              )}
            </div>
            
            <button
              onClick={handleRemoveVoucher}
              style={{
                backgroundColor: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '0.8em',
                cursor: 'pointer',
                marginLeft: 12
              }}
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
    
    {/* Confirmation Dialog rendered outside sticky-search-container */}
    {showConfirmDialog && createPortal(
      <div style={{
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh',
        background: 'rgba(0,0,0,0.6)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 9999,
        margin: 0,
        padding: 0
      }}>
        <div style={{ 
          background: '#fff', 
          padding: '28px 24px', 
          borderRadius: 12, 
          minWidth: 320, 
          maxWidth: 400,
          width: '90vw',
          boxShadow: '0 8px 32px rgba(44,62,80,0.15)',
          fontFamily: 'Segoe UI, Arial, sans-serif',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 16
          }}>
            <img 
              src="/img/favicon.svg" 
              alt="La Casita" 
              style={{
                width: 24,
                height: 24,
                marginRight: 8
              }}
            />
            <h3 style={{ 
              color: '#dc3545', 
              margin: 0,
              fontSize: '1.2em'
            }}>
              Remove Voucher
            </h3>
          </div>
          
          <p style={{
            margin: '0 0 20px 0',
            color: '#6c757d',
            lineHeight: 1.5
          }}>
            Are you sure you want to remove the applied voucher? This will remove any discount from your booking.
          </p>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 12,
            paddingTop: 16,
            borderTop: '1px solid #e3f0ff'
          }}>
            <button 
              onClick={handleCancelConfirm}
              style={{
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 20px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmAction}
              style={{
                background: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 20px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Remove Voucher
            </button>
          </div>
        </div>
      </div>, 
      document.body
    )}
  </>
  );
};

export default VoucherApplication;
